export interface TimeoutWrapperOptions {
	onCleanup?: () => void;
	onTimeout: () => void;
	timeout: number;
}

export interface TimeoutWrapper {
	promise: Promise<void>;
	resetTimeout: () => void;
}

/**
 * Creates a timeout wrapper that can be reset during execution.
 *
 * @param promise - The promise to wrap with timeout
 * @param options - Configuration options for the timeout
 * @returns Object containing the wrapped promise and reset function
 * ```
 */
export function createTimeoutWrapper(
	promise: Promise<void>,
	options: TimeoutWrapperOptions,
): TimeoutWrapper {
	const { timeout, onTimeout, onCleanup } = options;

	let timeoutHandle: NodeJS.Timeout | undefined;
	let timeoutResolve: (() => void) | undefined;
	let isCompleted = false;

	// Function to start/restart the timeout
	const resetTimeout = () => {
		if (isCompleted) {
			return;
		}
		if (timeoutHandle) {
			clearTimeout(timeoutHandle);
		}
		timeoutHandle = setTimeout(() => {
			if (!isCompleted) {
				onTimeout();
				timeoutResolve?.();
			}
		}, timeout);
	};

	const timeoutPromise = new Promise<void>((resolve) => {
		timeoutResolve = resolve;
	});

	resetTimeout();

	// Race the original promise against the timeout
	const wrappedPromise = Promise.race([promise, timeoutPromise]).finally(() => {
		isCompleted = true;
		if (timeoutHandle) {
			clearTimeout(timeoutHandle);
		}
		onCleanup?.();
	});

	return {
		resetTimeout,
		promise: wrappedPromise,
	};
}
